import * as THREE from 'three';
import type { Tour } from '../content/schema';

/**
 * tourSpline — CatmullRomCurve3 factory for Plan 05-03 guided tours.
 *
 * Mirrors the streetcarSpline factory invariants:
 *   - curveType='centripetal' — avoids loops/cusps when waypoints are
 *     unevenly spaced
 *   - closed=false — tours are one-way walkthroughs, not loops
 *   - tension=0.5 — standard relaxed curve
 *   - waypoints.length >= 2 guard
 *
 * Accepts the full Tour object (not just waypoints) so the signature stays
 * aligned with how TourRig consumes manifest.tours entries — a Tour always
 * carries its own id + durationS + steps, and downstream code frequently
 * wants the tour metadata alongside the curve.
 *
 * Arc-length uniformity is inherent via THREE.CatmullRomCurve3.getPointAt()
 * (per 05-RESEARCH.md Pitfall 1). TourRig consumes getPointAt, NEVER getPoint.
 */
export function buildTourCurve(tour: Tour): THREE.CatmullRomCurve3 {
  if (tour.waypoints.length < 2) {
    throw new Error(
      `buildTourCurve: tour '${tour.id}' waypoints.length must be >= 2; got ${tour.waypoints.length}`,
    );
  }
  const points = tour.waypoints.map(
    ([x, y, z]) => new THREE.Vector3(x, y, z),
  );
  return new THREE.CatmullRomCurve3(
    points,
    /* closed */ false,
    /* curveType */ 'centripetal',
    /* tension */ 0.5,
  );
}
